import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConfigLoadError } from '@/lib/configLoader';
/**
 * Компонент для отображения ошибок загрузки конфигурации
 * Предоставляет понятную информацию пользователю без технических деталей
 */
export const ConfigErrorFallback = ({ error, onRetry }) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const isConfigLoadError = error instanceof ConfigLoadError;
    // Определяем тип ошибки для показа соответствующего сообщения
    const getErrorType = () => {
        if (isConfigLoadError) {
            if (error.httpStatus === 404)
                return 'not-found';
            if (error.httpStatus && error.httpStatus >= 500)
                return 'server-error';
            if (error.cause && error.cause.message?.includes('fetch'))
                return 'network-error';
            return 'config-error';
        }
        return 'unknown-error';
    };
    const errorType = getErrorType();
    // Сообщения для пользователей (без технических деталей)
    const getUserMessage = () => {
        switch (errorType) {
            case 'not-found':
                return 'Конфигурация для данного региона временно недоступна.';
            case 'server-error':
                return 'Сервис временно недоступен. Мы работаем над решением проблемы.';
            case 'network-error':
                return 'Проблема с подключением к интернету. Проверьте соединение и попробуйте снова.';
            case 'config-error':
                return 'Произошла ошибка при загрузке настроек приложения.';
            default:
                return 'Произошла техническая ошибка. Попробуйте обновить страницу.';
        }
    };
    // Иконка в зависимости от типа ошибки
    const getErrorIcon = () => {
        switch (errorType) {
            case 'not-found':
                return '🏢';
            case 'server-error':
                return '⚠️';
            case 'network-error':
                return '🌐';
            case 'config-error':
                return '⚙️';
            default:
                return '❌';
        }
    };
    // Дополнительные действия в зависимости от типа ошибки
    const getActionButtons = () => {
        const buttons = [
            _jsx("button", { onClick: onRetry || (() => window.location.reload()), className: "px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium", children: "\u041F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C \u0441\u043D\u043E\u0432\u0430" }, "retry")
        ];
        if (errorType === 'network-error') {
            buttons.push(_jsx("button", { onClick: () => {
                    if (navigator.onLine) {
                        alert('Соединение с интернетом активно. Попробуйте обновить страницу.');
                    }
                    else {
                        alert('Нет подключения к интернету. Проверьте настройки сети.');
                    }
                }, className: "px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C \u0441\u043E\u0435\u0434\u0438\u043D\u0435\u043D\u0438\u0435" }, "check-connection"));
        }
        return buttons;
    };
    return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50", children: _jsx("div", { className: "max-w-md w-full mx-4", children: _jsxs("div", { className: "bg-white rounded-lg shadow-lg p-8 text-center", children: [_jsx("div", { className: "text-6xl mb-4", children: getErrorIcon() }), _jsx("h1", { className: "text-2xl font-bold text-gray-800 mb-4", children: "\u0421\u0430\u0439\u0442 \u0432\u0440\u0435\u043C\u0435\u043D\u043D\u043E \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D" }), _jsx("p", { className: "text-gray-600 mb-6 leading-relaxed", children: getUserMessage() }), _jsx("div", { className: "space-y-3", children: getActionButtons() }), process.env.NODE_ENV === 'development' && (_jsxs("details", { className: "mt-6 text-left", children: [_jsx("summary", { className: "cursor-pointer text-sm text-gray-500 hover:text-gray-700", children: "\u0422\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F (dev \u0440\u0435\u0436\u0438\u043C)" }), _jsxs("div", { className: "mt-3 p-3 bg-gray-100 rounded text-sm text-gray-700 font-mono", children: [_jsxs("div", { children: [_jsx("strong", { children: "\u0422\u0438\u043F \u043E\u0448\u0438\u0431\u043A\u0438:" }), " ", errorType] }), _jsxs("div", { children: [_jsx("strong", { children: "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435:" }), " ", errorMessage] }), isConfigLoadError && error.url && (_jsxs("div", { children: [_jsx("strong", { children: "URL:" }), " ", error.url] })), isConfigLoadError && error.httpStatus && (_jsxs("div", { children: [_jsx("strong", { children: "HTTP \u0441\u0442\u0430\u0442\u0443\u0441:" }), " ", error.httpStatus] })), _jsxs("div", { children: [_jsx("strong", { children: "Timestamp:" }), " ", new Date().toISOString()] })] })] })), _jsx("div", { className: "mt-8 pt-6 border-t border-gray-200", children: _jsx("p", { className: "text-sm text-gray-500", children: "\u0415\u0441\u043B\u0438 \u043F\u0440\u043E\u0431\u043B\u0435\u043C\u0430 \u043F\u043E\u0432\u0442\u043E\u0440\u044F\u0435\u0442\u0441\u044F, \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u0432 \u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0443\u044E \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0443" }) })] }) }) }));
};
export default ConfigErrorFallback;
